import { styled } from '@mui/material/styles'
import { Box, Card, Collapse, IconButton, Typography } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import { FC, ReactNode, useState } from 'react'
import { useTranslate } from 'react-admin'
import { border } from '../layout/theme/border'
import { themeColor } from '../layout/theme/color'

const PREFIX = 'xxllncFilterAside'

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  content: `${PREFIX}-content`,
  toggle: `${PREFIX}-toggle`,
}

const STORAGE_KEY = 'sa_filter_aside_open'

interface FilterAsideProps {
  children: ReactNode
}

export const FilterAside: FC<FilterAsideProps> = ({ children }) => {
  const [open, setOpen] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true')
  const translate = useTranslate()

  const toggle = (value: boolean) => {
    setOpen(value)
    localStorage.setItem(STORAGE_KEY, String(value))
  }

  return (
    <StyledRoot className={classes.root}>
      {!open && (
        <IconButton
          className={classes.toggle}
          onClick={() => toggle(true)}
          size="small"
          title={translate('filters.show')}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      )}
      <Collapse in={open} orientation="horizontal" unmountOnExit>
        <Card className={classes.content} elevation={0}>
          <Box className={classes.header}>
            <FilterListIcon sx={{ fontSize: 16, color: themeColor.slate40 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: themeColor.slate60, letterSpacing: '0.02em' }}>
              {translate('filters.title')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => toggle(false)} sx={{ color: themeColor.slate40 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {children}
        </Card>
      </Collapse>
    </StyledRoot>
  )
}

const StyledRoot = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    order: -1,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  [`& .${classes.toggle}`]: {
    backgroundColor: '#fff',
    border: border.thin + ' solid ' + themeColor.slate10,
    borderRadius: border.card.radius,
    boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.02)',
    padding: theme.spacing(1),
    marginRight: theme.spacing(2),
    color: themeColor.slate40,
    ['&:hover']: {
      backgroundColor: themeColor.slate05,
      color: themeColor.slate80,
    },
  },
  [`& .${classes.content}`]: {
    width: 220,
    marginRight: theme.spacing(2),
    backgroundColor: '#fff',
    border: border.thin + ' solid ' + themeColor.slate10,
    borderRadius: border.card.radius,
    boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.02)',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: border.thin + ' solid ' + themeColor.slate10,
  },
}))
